import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpenIcon,
  BookmarkIcon,
  BuildingOffice2Icon,
  CircleStackIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface ModuleData {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  features: string[];
  path: string;
  gradient: string;
  accentColor: string;
  lightBg: string;
}

const MODULES: ModuleData[] = [
  {
    icon: FolderOpenIcon,
    title: '项目管理',
    description: '集中管理所有标书项目，实时掌握各项目进度与状态，支持多维度筛选与搜索',
    features: ['项目列表与状态追踪', '按类型与进度筛选', '项目复制与快速重用', '支持搜索与排序'],
    path: '/projects',
    gradient: 'linear-gradient(145deg, #1d4ed8, #0369a1)',
    accentColor: '#3b82f6',
    lightBg: '#eff6ff',
  },
  {
    icon: BookmarkIcon,
    title: '模板管理',
    description: '沉淀优质标书模板，构建团队知识库，快速复用最佳实践，提升标书一致性',
    features: ['多类型标书模板库', '模板一键快速套用', '自定义章节内容', '持续积累优化'],
    path: '/templates',
    gradient: 'linear-gradient(145deg, #0f766e, #0369a1)',
    accentColor: '#14b8a6',
    lightBg: '#f0fdfa',
  },
  {
    icon: BuildingOffice2Icon,
    title: '主体管理',
    description: '统一管理投标主体公司信息，快速复用企业资质与人员数据，消除重复录入',
    features: ['公司基础信息管理', '法人与联系人录入', '资质证书信息记录', '多主体快速切换'],
    path: '/companies',
    gradient: 'linear-gradient(145deg, #b45309, #d97706)',
    accentColor: '#f59e0b',
    lightBg: '#fffbeb',
  },
  {
    icon: CircleStackIcon,
    title: '资源管理',
    description: '统一管理外部资源与参考资料，快速查阅行业标准与政府采购信息',
    features: ['外部 API 资源配置', '参考文档资料库', '行业标准文件索引', '政府采购网链接'],
    path: '/resources',
    gradient: 'linear-gradient(145deg, #0e7490, #0f766e)',
    accentColor: '#06b6d4',
    lightBg: '#ecfeff',
  },
];

const ModuleCard: React.FC<{ mod: ModuleData; delay: number; visible: boolean }> = ({
  mod,
  delay,
  visible,
}) => {
  const navigate = useNavigate();
  const Icon = mod.icon;

  return (
    <div
      className="group bg-white rounded-3xl p-8 border border-slate-100 hover:border-transparent hover:shadow-2xl cursor-pointer relative overflow-hidden transition-all duration-300"
      onClick={() => navigate(mod.path)}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms, box-shadow 0.3s ease, border-color 0.3s ease`,
      }}
    >
      <div
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(135deg, ${mod.accentColor}06, ${mod.accentColor}02)` }}
      />

      <div className="relative z-10">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
          style={{
            background: mod.gradient,
            boxShadow: `0 8px 24px ${mod.accentColor}30`,
          }}
        >
          <Icon className="w-7 h-7 text-white" />
        </div>

        <h3 className="text-xl font-bold text-slate-900 mb-2">{mod.title}</h3>
        <p className="text-slate-500 text-sm leading-relaxed mb-6">{mod.description}</p>

        <div className="grid grid-cols-2 gap-y-2 gap-x-3 mb-7">
          {mod.features.map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm text-slate-600">
              <div
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: mod.accentColor }}
              />
              {f}
            </div>
          ))}
        </div>

        <div
          className="flex items-center gap-1.5 text-sm font-semibold transition-all duration-200 group-hover:gap-2.5"
          style={{ color: mod.accentColor }}
        >
          进入管理
          <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
        </div>
      </div>
    </div>
  );
};

const ManagementSection: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="management" className="py-28 bg-slate-50" ref={sectionRef}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6"
            style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}
          >
            <span className="text-sm font-semibold text-blue-600">配套功能模块</span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight transition-all duration-700"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
            }}
          >
            全面的项目管理能力
          </h2>
          <p
            className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed transition-all duration-700"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transitionDelay: '100ms',
            }}
          >
            除核心创建流程外，提供完善的配套管理功能，让团队高效协作
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {MODULES.map((mod, i) => (
            <ModuleCard
              key={mod.title}
              mod={mod}
              delay={i * 100}
              visible={visible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ManagementSection;
